<template>
  <div>
    <slot name="before-divider"></slot>

    <slot name="divider"><vs-divider/></slot>

    <div
      v-if="$slots['after-divider']"
      class="mb-3">
      <slot name="after-divider"></slot>
    </div>

    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="flex flex-wrap items-center justify-between">
          <div class="flex items-center">
            <template v-for="(action, index) in actions">
              <!-- Show tooltip only on non-touch devices -->
              <vx-tooltip
                v-if="!isTouchDevice"
                :key="index"
                :ref="`tooltip-${index}`"
                :text="$t(action.text) | sentencecase"
                position="top"
                class="inline-block">
                <vs-button
                  :class="[index > 0 ? 'ml-2' : '']"
                  radius
                  :color="action.color"
                  size="large"
                  type="flat"
                  icon-pack="feather"
                  :icon="action.icon"
                  @click="handleActionClick(action, index)">
                </vs-button>
              </vx-tooltip>
              <!-- No tooltip wrapper on touch devices -->
              <vs-button
                v-else
                :key="index"
                :class="[index > 0 ? 'ml-2' : '']"
                radius
                :color="action.color"
                size="large"
                type="flat"
                icon-pack="feather"
                :icon="action.icon"
                @click="handleActionClick(action, index)">
              </vs-button>
            </template>
          </div>

          <div class="flex items-center">
            <vs-button
              v-if="!hideSave"
              ref="confirmModalButton"
              @click="$emit('save')"
              :disabled="disabledSave">
              <slot name="save">{{ $t('$General.Save') }}</slot>
            </vs-button>
            <vs-button
              v-avoid-key-tab-event
              :class="hideSave ? '' : 'ml-4'"
              color="danger"
              class="vs-button-cancel-flat"
              @click="$emit('cancel')">
              <slot name="cancel">{{ $t('$General.Cancel') }}</slot>
            </vs-button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/**
 * Component to show save and cancel buttons on footer of forms
 *
 * @module views/components/BaseFormFooterActionButtons
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 * @vue-prop {boolean} disabledSave - indicate if save button is disabled
 * @vue-prop {string} actions - action buttons to show
 * @vue-event {void} emitAction - called to emit an action
 */
export default {
  name: 'BaseFormFooterActionButtons',
  props: {
    disabledSave: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSave: {
      type: Boolean,
      required: false,
      default: false,
    },
    actions: {
      type: Array,
      required: false,
      validator(actions) {
        return actions.every(
          (a) => 'name' in a && 'color' in a
            && 'text' in a && 'position' in a && 'icon' in a,
        );
      },
      default() {
        return [];
      },
    },
  },
  computed: {
    isTouchDevice() {
      // Detect if device has touch capability
      return (
        ('ontouchstart' in window)
        || (navigator.maxTouchPoints > 0)
        || (navigator.msMaxTouchPoints > 0)
      );
    },
  },
  methods: {
    emitAction(action) {
      this.$emit('action', action.name);
    },
    handleActionClick(action, index) {
      // Force close the tooltip before executing action
      this.$nextTick(() => {
        // Remove focus from button to ensure tooltip closes
        if (document.activeElement) {
          document.activeElement.blur();
        }
      });
      this.emitAction(action);
    },
  },
};
</script>

<style lang="scss" scoped>
// Hide tooltips on touch devices to prevent them from sticking (media query for real devices)
@media (hover: none) {
  ::v-deep .vx-tooltip {
    display: none !important;
  }
}

// Hide tooltips when a modal/dialog is open (unscoped to affect global tooltips)
::v-deep .vs-dialog-dark,
::v-deep .vs-popup {
  ~ * .vx-tooltip,
  & ~ .vx-tooltip {
    display: none !important;
  }
}
</style>

<style lang="scss">
// Global styles for tooltip management

// Hide tooltips when any modal is active
body.vs-dialog-open .vx-tooltip {
  display: none !important;
}

// Hide tooltips on touch devices (class-based detection - works in Chrome simulator)
body.touch-device .vx-tooltip {
  display: none !important;
}
</style>
